import { JSX, useEffect } from 'react';
import { GetServerSideProps } from 'next';
import NotFound from 'src/NotFound';
import Layout from 'src/Layout';
import { SitecoreContext, ComponentPropsContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { componentBuilder } from 'temp/componentBuilder';
import { useUrlChangeListener } from '../shared/useRouteChangeListener';
import { PromptForMissingDatasource } from '@/shared/PromptForMissingDatasource';

const SitecorePage = ({
  notFound,
  componentProps = {},
  layoutData,
  headLinks,
  analyticsScriptSource,
}: SitecorePageProps): JSX.Element => {
  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  useUrlChangeListener(analyticsScriptSource);

  if (notFound || !layoutData?.sitecore?.route) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  const brokenDatasources = layoutData.sitecore.context.brokenDatasources;

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
      >
        {brokenDatasources && (
          <>
            {brokenDatasources?.map((broken) => (
              <PromptForMissingDatasource
                reason="datasource-unresolved"
                rendering={broken.rendering}
                datasource={broken.datasource}
                alertType="error"
                key={broken.rendering}
              />
            ))}
          </>
        )}

        <Layout layoutData={layoutData} headLinks={headLinks} />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// This function gets called at request time on server-side.
export const getServerSideProps: GetServerSideProps = async (context) => {
  const props = await sitecorePagePropsFactory.create(context);

  const cacheMaxAge =
    'public, max-age=' + (props.notFound ? process.env.PAGE_404_MAX_AGE : process.env.PAGE_MAX_AGE);
  context.res.setHeader('Cache-Control', cacheMaxAge);

  const analyticsScriptSource = process.env.ANALYTICS_SCRIPT_SOURCE ?? '';

  if (props.notFound) {
    // When 404, return notFound only
    return { notFound: true };
  }

  return {
    props: {
      ...props,
      analyticsScriptSource,
    },
  };
};

export default SitecorePage;
